export type ServiceLogo = {
  name: string;
  /** simpleicons slug used with cdn.simpleicons.org */
  slug: string;
};

export type Service = {
  id: string;
  slug: string;
  icon: string;
  name: string;
  tagline: string;
  summary: string;
  description: string;
  outcomes: string[];
  capabilities: string[];
  /** Brand/system logos relevant to this service */
  logos: ServiceLogo[];
};

function logo(name: string, slug: string): ServiceLogo {
  return { name, slug };
}

export const services: Service[] = [
  {
    id: 'svc_email',
    slug: 'email-communication',
    icon: 'mail',
    name: 'Email & Communication Automation',
    tagline: 'Inbox triage, drafting, and responses that sound like your team',
    summary: 'Automate email triage, drafting, and responses.',
    description:
      'We wire AI into the channels your operators already live in — Gmail, Outlook, shared inboxes, and help desks — so routine mail gets classified, drafted, and routed without a human copy-paste loop. Priority threads surface fast. Brand voice stays consistent. Follow-ups stop falling through the cracks.',
    outcomes: [
      'Hours back every week from inbox thrash',
      'Faster first response on priority threads',
      'Consistent tone across the team',
      'Clear audit trail of what the agent did',
    ],
    capabilities: [
      'Priority triage and label routing',
      'Draft replies in your brand voice',
      'Thread summarization for handoff',
      'Escalation rules for human review',
      'CRM / EspoCRM logging on send',
    ],
    logos: [
      logo('Gmail', 'gmail'),
      logo('Outlook', 'microsoftoutlook'),
      logo('Microsoft 365', 'microsoft365'),
      logo('Slack', 'slack'),
      logo('Microsoft Teams', 'microsoftteams'),
      logo('Google Workspace', 'google'),
    ],
  },
  {
    id: 'svc_support',
    slug: 'customer-support',
    icon: 'smart_toy',
    name: 'AI-Powered Customer Support',
    tagline: 'Chatbots and support systems in your brand voice, 24/7',
    summary: 'Chatbots and support systems in your brand voice, 24/7.',
    description:
      'Stand up support agents that answer common questions, pull account context, and escalate cleanly when a human should take the stick. Built around your knowledge base, tone guide, and escalation paths — not a generic widget bolted on overnight.',
    outcomes: [
      'Round-the-clock coverage without staffing night shifts',
      'Lower ticket volume on repetitive asks',
      'Smoother handoff with full conversation context',
      'Measurable deflection and CSAT gains',
    ],
    capabilities: [
      'Web chat + messaging channel agents',
      'Knowledge-base grounded answers',
      'Ticket creation and status updates',
      'Human takeover with full transcript',
      'Analytics on deflection and containment',
    ],
    logos: [
      logo('Zendesk', 'zendesk'),
      logo('Intercom', 'intercom'),
      logo('Help Scout', 'helpscout'),
      logo('HubSpot', 'hubspot'),
      logo('WhatsApp', 'whatsapp'),
      logo('Discord', 'discord'),
    ],
  },
  {
    id: 'svc_data',
    slug: 'data-entry',
    icon: 'analytics',
    name: 'Data Entry & Processing',
    tagline: 'Intelligent automation that learns your processes',
    summary: 'Intelligent automation that learns your processes.',
    description:
      'Extract, validate, and enter data from PDFs, forms, spreadsheets, and inboxes into the systems of record your team already trusts. We design human checkpoints where accuracy matters and full automation where volume is the bottleneck.',
    outcomes: [
      'Eliminate repetitive copy-paste work',
      'Fewer entry errors and rework loops',
      'Faster turnaround on high-volume intake',
      'Structured data ready for reporting',
    ],
    capabilities: [
      'Document and form extraction',
      'Validation rules and exception queues',
      'CRM / ERP / spreadsheet write-back',
      'Batch and real-time processing modes',
      'Confidence scoring with human review',
    ],
    logos: [
      logo('Microsoft Excel', 'microsoftexcel'),
      logo('Google Sheets', 'googlesheets'),
      logo('n8n', 'n8n'),
      logo('Power Automate', 'powerautomate'),
      logo('Airtable', 'airtable'),
      logo('Google Drive', 'googledrive'),
    ],
  },
  {
    id: 'svc_calendar',
    slug: 'calendar-meetings',
    icon: 'calendar_month',
    name: 'Meeting & Calendar Management',
    tagline: 'Scheduling, reminders, and follow-ups on autopilot',
    summary: 'Scheduling, reminders, and follow-ups on autopilot.',
    description:
      'From finding a slot to sending prep notes and post-meeting follow-ups, we automate the calendar choreography that burns operator time. Works with the tools you already use and the rules your team actually follows.',
    outcomes: [
      'Fewer back-and-forth scheduling threads',
      'On-time reminders and prep packets',
      'Consistent post-meeting follow-up',
      'Cleaner calendar hygiene across the team',
    ],
    capabilities: [
      'Availability-aware scheduling',
      'Reminder and prep sequences',
      'Meeting note capture hooks',
      'Follow-up task creation',
      'Calendar + CRM sync',
    ],
    logos: [
      logo('Google Calendar', 'googlecalendar'),
      logo('Outlook', 'microsoftoutlook'),
      logo('Zoom', 'zoom'),
      logo('Calendly', 'calendly'),
      logo('Microsoft Teams', 'microsoftteams'),
      logo('Notion', 'notion'),
    ],
  },
  {
    id: 'svc_sales',
    slug: 'sales-leads',
    icon: 'handshake',
    name: 'Sales & Lead Management',
    tagline: 'Qualify leads, automate outreach, nurture prospects',
    summary: 'Qualify leads, automate outreach, nurture prospects.',
    description:
      'Keep pipeline moving without drowning reps in busywork. We automate qualification, personalized outreach, and nurture cadences so humans spend time on conversations that close — not on chasing stale leads.',
    outcomes: [
      'Faster lead response times',
      'Higher follow-up consistency',
      'Cleaner pipeline visibility',
      'More selling time, less admin',
    ],
    capabilities: [
      'Lead scoring and routing',
      'Personalized outreach sequences',
      'Nurture cadences by stage',
      'CRM activity logging',
      'Handoff rules for warm opportunities',
    ],
    logos: [
      logo('Salesforce', 'salesforce'),
      logo('HubSpot', 'hubspot'),
      logo('LinkedIn', 'linkedin'),
      logo('ClickUp', 'clickup'),
      logo('Mailchimp', 'mailchimp'),
      logo('Zapier', 'zapier'),
    ],
  },
  {
    id: 'svc_workflow',
    slug: 'workflow-integration',
    icon: 'hub',
    name: 'Workflow Automation',
    tagline: 'Connect tools for seamless data flow across your platforms and processes',
    summary: 'Connect tools for seamless data flow across your platforms and processes.',
    description:
      'Glue the systems you already own — CRM, email, support, billing, ops tools — into reliable workflows. We design durable integrations with clear ownership, logging, and failure handling so automation does not become a black box.',
    outcomes: [
      'One source of truth across tools',
      'Fewer manual handoffs between teams',
      'Faster process cycle times',
      'Integrations you can operate and trust',
    ],
    capabilities: [
      'API and webhook orchestration',
      'Event-driven and scheduled jobs',
      'Error handling and retries',
      'Observability and run history',
      'n8n / custom worker patterns',
    ],
    logos: [
      logo('n8n', 'n8n'),
      logo('Zapier', 'zapier'),
      logo('Make', 'make'),
      logo('Power Automate', 'powerautomate'),
      logo('Shopify', 'shopify'),
      logo('Stripe', 'stripe'),
    ],
  },
];

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}

/** Self-hosted brand SVGs (fill colors baked in so they render on the dark theme). */
export function serviceLogoUrl(slug: string): string {
  return `/assets/service-logos/${slug}.svg`;
}
